import { create } from 'zustand';
import type { Prefs, PublicUser } from '@scribbleplot/shared';
import { ApiError, api } from './api';
import { DEFAULT_ACCENT } from '../tokens';

/**
 * Signed-in user and their preferences.
 *
 * Preference writes are optimistic: the accent swatch and grid toggle should
 * feel instant, and a failed save is reverted with the error surfaced.
 */

type SessionStore = {
  user: PublicUser | null;
  /** `true` until the initial `/api/me` probe settles. */
  loading: boolean;
  /** Single sign-on availability and the label for its button. */
  sso: { enabled: boolean; name: string };
  allowSignup: boolean;
  /** False when the instance has no SMTP configured. */
  mailEnabled: boolean;

  bootstrap: () => Promise<void>;
  setUser: (user: PublicUser | null) => void;
  signOut: () => Promise<void>;
  updatePrefs: (prefs: Prefs) => Promise<void>;
  updateName: (name: string) => Promise<void>;
};

export const useSession = create<SessionStore>((set, get) => ({
  user: null,
  loading: true,
  sso: { enabled: false, name: 'SSO' },
  allowSignup: true,
  mailEnabled: false,

  bootstrap: async () => {
    // Both probes are independent; a failed session check is the normal
    // anonymous case and must not block reading the auth config.
    const [config, me] = await Promise.allSettled([api.authConfig(), api.me()]);

    if (config.status === 'fulfilled') {
      set({
        sso: config.value.sso,
        allowSignup: config.value.allowSignup,
        mailEnabled: config.value.mailEnabled,
      });
    }
    set({
      user: me.status === 'fulfilled' ? me.value.user : null,
      loading: false,
    });
  },

  setUser: (user) => set({ user }),

  signOut: async () => {
    try {
      await api.logout();
    } finally {
      // Clear locally even if the request failed — the cookie may already be gone.
      set({ user: null });
    }
  },

  updatePrefs: async (prefs) => {
    const previous = get().user;
    if (!previous) return;
    set({ user: { ...previous, prefs: { ...previous.prefs, ...prefs } } });
    try {
      const { user } = await api.updateMe({ prefs });
      set({ user });
    } catch (err) {
      set({ user: previous });
      throw err instanceof ApiError ? err : new Error('Could not save your preferences.');
    }
  },

  updateName: async (name) => {
    const previous = get().user;
    if (!previous) return;
    set({ user: { ...previous, name } });
    try {
      const { user } = await api.updateMe({ name });
      set({ user });
    } catch (err) {
      set({ user: previous });
      throw err instanceof ApiError ? err : new Error('Could not save your name.');
    }
  },
}));

/** Accent colour, falling back to the brand default for anonymous viewers. */
export const useAccent = (): string => useSession((s) => s.user?.prefs?.accent ?? DEFAULT_ACCENT);

export const usePref = <K extends keyof Prefs>(key: K, fallback: NonNullable<Prefs[K]>): NonNullable<Prefs[K]> =>
  useSession((s) => {
    const value = s.user?.prefs?.[key];
    return (value ?? fallback) as NonNullable<Prefs[K]>;
  });

/** Initials shown in the avatar chip. */
export const initialsOf = (name: string | undefined): string => (name || 'U').trim().charAt(0).toUpperCase() || 'U';
